import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import { PsychologistProvider } from './contexts/PsychologistContext';
import { AcuityMigrationProvider } from './contexts/AcuityMigrationContext';
import { AppRoutes } from './routes';
import { ToastContainer } from './components/common/Toast/ToastContainer';
import AcuityMigrationWelcomeModal from './components/common/AcuityMigrationWelcomeModal';
import { TooltipProvider } from './components/ui/tooltip';
import { saveSession, type JWTUserData } from './hooks/api/auth/tokenManager';

/**
 * Maneja el redirect de impersonación: ?token=xxx
 * Decodifica el JWT, guarda la sesión y redirige a /home.
 * Se ejecuta a nivel de módulo para que React nunca intente montar
 * el árbol de componentes cuando hay una redirección en vuelo.
 */
function handleImpersonateToken(): boolean {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (!token) return false;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1]));

    const firstName = payload.name || payload.first_name || '';
    const lastName = payload.surname || payload.last_name || '';
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName;

    const userData: JWTUserData = {
      id: payload.id || payload.user_id || '',
      email: payload.email || '',
      name: fullName,
      surname: lastName,
    };

    saveSession(token, userData, payload.scope || []);
    // Limpiar foto de Google del usuario anterior para no mostrarla en sesión impersonada
    try { localStorage.removeItem('google_user'); localStorage.removeItem('google_access_token'); } catch { /* noop */ }
    window.location.href = '/home';
    return true;
  } catch {
    return false;
  }
}

// Ejecutar antes de cualquier render. Si hay token, redirige y el módulo queda en espera.
const isRedirecting = handleImpersonateToken();

function App() {
  if (isRedirecting) return null;

  return (
    <AuthProvider>
      <PsychologistProvider>
        <AcuityMigrationProvider>
          <TooltipProvider>
            <AppRoutes />
            <AcuityMigrationWelcomeModal />
            <ToastContainer />
          </TooltipProvider>
        </AcuityMigrationProvider>
      </PsychologistProvider>
    </AuthProvider>
  );
}

export default App;
